import AssistantStore from "@/helpers/assistant-store.js";
import CMS from "@/helpers/cms/CMS.js";
import CmsPage from "@/helpers/cms/CmsPage.js";
import CmsWebsite from "@/helpers/cms/CmsWebsite.js";
import router from "@/router.js";
import Vue from 'vue';
import swal from "sweetalert";
import Vuex from 'vuex';
import {getLocale, normalizeLocaleCode, getFallbackLocale, setFallbackLocale, setLocale} from "@vue-components/helpers/I18nPluginHelper.js";
import Network from "@vue-components/helpers/Network.js";
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";
import GoogleFontStore from "@/helpers/google-font-store.js";
import MapStore from "@/helpers/maps/map-store.js";
import ClipboardStore from "@/helpers/clipboard/clipboard-store.js";
import TextCompareStore from "@/helpers/text-compare-store.js";
import moment from "moment";

Vue.use(Vuex);

// TODO: move to utils.js?
const normalizeLanguageCodes = (languages = []) => {
    if (!Array.isArray(languages)) return [];
    const normalized = [];
    const seen = new Set();
    for (const lang of languages) {
        const code = normalizeLocaleCode(lang);
        if (!code) continue;
        const key = code.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        normalized.push(code);
    }
    return normalized;
};

export default new Vuex.Store({
    state: {
        applicationLoading: false,
        applicationReady: false,
        dealerid: null,
        page: null,
        pages: {},
        website: null,
        cache: `no-cache`, // for editor advanced purpose
        websiteStyleVersion: 0,
        cms: null,
        user: null,
        activeTutorial: null,
        env: localStorage.getItem(`env`) || `dev`,
        sessionData: {
            medium: null,
            source: null,
            campaign: null,
            utms: {}, // Google Standard UTM parameters
        },
        editMode: true,
        previewLanguage: null,
        // mobile | tablet | kiosk | desktop
        previewMode: `desktop`,
        displayMode: `desktop`,
        languageSelectModal: false,
        feedbackModal: {
            visible: false,
            data: null
        },
        previewDeviceFrame: true,
        editRawHtml: false,
        blockHtmlEditorEnabled: {}, // Track which blocks have HTML editor enabled: { blockId: true/false }
        zoom: 100,
        accessibility: {
            wheelchair: JSON.parse(localStorage.getItem(`wheelchair`)) || false
        },
        display: {
            review: false,
            comparison: false,
        },
        idleTimeout: false,
        playerId: false,
        isKioskDevice: false,
        isMobileLayout: false,
        isSmallLayout: false,
        isTabletLayout: false,
        isMobileOrTabletLayout: false,
        isLargeLayout: false,
        isExtraLargeLayout: false,
        cookieConsent: {
            decided: localStorage.getItem(`cookieConsentDecided`) === `true`,
            analytics: localStorage.getItem(`cookieConsentAnalytics`) === `true`,
            functional: localStorage.getItem(`cookieConsentFunctional`) !== `false`
        }
    },
    getters: {
        cms(state) {
            return state.cms ? state.cms : null;
        },
        currentPage(state) {
            return state.page ? state.page : null;
        },
        currentWebsite(state) {
            return state.website ? state.website : null;
        },
        currentUser(state) {
            return state.user ? state.user : null;
        },
        isAuthenticated(state) {
            return !!state.user;
        },
        displayLanguage(state) {
            if (state.previewLanguage) {
                return state.previewLanguage;
            } else {
                return getLocale();
            }
        },
        env(state) {
            return state.env;
        }
    },
    mutations: {
        /**
         * Force update all components for editor purpose
         * such as applying batch translation or suggestions
         */
        updateCache(state) {
            state.cache = generateGuid();
        },
        setAccessibilityWheelchair(state, wheelchair) {
            state.accessibility.wheelchair = wheelchair;
            localStorage.setItem(`wheelchair`, JSON.stringify(wheelchair));
        },
        showFeedbackModal(state, data = null) {
            state.feedbackModal.visible = true;
            state.feedbackModal.data = data;
        },
        updateDisplayMode(state) {
            if (state.previewMode !== `desktop`) {
                state.displayMode = state.previewMode;
            } else if (state.isKioskDevice) {
                state.displayMode = `kiosk`;
            } else if (state.isMobileLayout) {
                state.displayMode = `mobile`;
            } else if (state.isTabletLayout) {
                state.displayMode = `tablet`;
            } else {
                state.displayMode = `desktop`;
            }
        },
        setZoom(state, zoom) {
            state.zoom = zoom;
        },
        setCookieConsent(state, consent = {}) {
            state.cookieConsent = {
                ...state.cookieConsent,
                ...consent
            };
        },
        toggleBlockHtmlEditor(state, blockId) {
            if (state.blockHtmlEditorEnabled[blockId]) {
                Vue.delete(state.blockHtmlEditorEnabled, blockId);
            } else {
                Vue.set(state.blockHtmlEditorEnabled, blockId, true);
            }
        },
        setCms(state, cms) {
            state.cms = cms;
        },
        setWebsite(state, website) {
            try {
                if (!website) {
                    return;
                }
                website = new CmsWebsite(website);
                state.website = website;
                state.dealerid = state.website.dealerid;

                /* Locale handling */
                const fallback = getFallbackLocale();
                if (!state.website.settings) {
                    state.website.settings = {languages: [fallback]};
                }
                const normalizedLanguages = normalizeLanguageCodes(state.website.settings.languages);
                state.website.settings.languages = normalizedLanguages.length ? normalizedLanguages : [fallback];
                const normalizedFallback = normalizeLocaleCode(state.website.settings.fallbackLanguage) || fallback;
                state.website.settings.fallbackLanguage = normalizedFallback;
                setFallbackLocale(normalizedFallback);

                const storedPreview = normalizeLocaleCode(localStorage.getItem(`previewLanguage`));
                const storedAllowed = storedPreview && state.website.settings.languages.includes(storedPreview) ? storedPreview : null;
                const requestedLocale = normalizeLocaleCode(state.urlLanguage);
                const urlLocale = requestedLocale && state.website.settings.languages.includes(requestedLocale) ? requestedLocale : null;

                // Prefer URL lang, then stored preview language, then fallback language
                const localeToUse = urlLocale || storedAllowed || normalizedFallback;
                console.log(`Setting locale to: `, localeToUse);

                setLocale(localeToUse);
                moment.locale(localeToUse);

                localStorage.setItem(`websiteId`, state.website.id);

                state.website.initWebsite();
                state.website.applyStyle();

                state.cms = new CMS(state.website);
                state.cms.initTypes(website.types);
                state.cms.addItems(website.items);

                if (state.website.map) {
                    this.commit(`mapEditor/setMap`, state.website.map);
                    state.website.map = state.mapEditor.map;
                }

                state.cms.updateDependencies();
                state.applicationReady = true;

                // for debug only
                window.cmsWebsite = website;
                window.cms = state.cms;
            } catch (e) {
                console.error(e);
                swal(`Network Error`, `Website is not valid`, `error`);
            }
        },
        setPages(state, pages) {
            try {
                state.website.pages = pages;
            } catch (e) {
                console.error(e);
                swal(`Network Error`, `Couldn't load pages`, `error`);
            }
        },
        setPage(state, data) {
            try {
                let page = new CmsPage(data.page, state.cms, data.route);
                if (!page.mediaProxy.visible && !state.editMode) {
                    if (page.fallbackLink) {
                        return router.push(page.fallbackLink.to || `/`);
                    }
                }
                state.page = page;
                window.cmsPage = state.page;
            } catch (e) {
                console.error(e);
                swal(`Network Error`, `Page is not valid`, `error`);
            }
        },
        checkWebsiteError(state, error) {
            console.error(error);
            state.website = null;
            window.lastError = error;
            if (error?.response?.data?.error === `PASSWORD_REQUIRED`) {
                state.applicationReady = true;
                router.push(`/password-required`);
            } else {
                swal(`Network Error`, `Couldn't load website`, `error`);
            }
        },
        startLogin(state) {
            state.loginStatus = `loading`
        },
        authSuccess(state, user) {
            state.loginStatus = `success`;
            state.user = user
        },
        notConnected(state) {
            state.loginStatus = `not_connected`;
        },
        authError(state) {
            state.loginStatus = `error`
        },
        loggedOut(state) {
            state.loginStatus = `logged_out`;
            state.user = null
        }
    },
    actions: {
        async loadWebsite({state, commit}, data = {id: null, domain: null}) {
            try {
                if (state.applicationReady) {
                    return;
                }
                let url = `/api/public/website`;
                if (state.user && data.id) {
                    url = `/api/website/${data.id}`;
                } else {
                    if (data.domain) {
                        url += `/${data.domain}`;
                    } else {
                        url += `/${window.location.hostname}`;

                        if (!data.id && localStorage.getItem(`websiteId`)) {
                            data.id = localStorage.getItem(`websiteId`);
                        }

                        if (data.id) {
                            url += `/${data.id}`;
                        }
                    }

                    if (localStorage.getItem(`password`)) {
                        url += `?password=${localStorage.getItem(`password`)}`;
                    }
                }

                // Note: NetworkV2 returns a broken error [Object object] instead of a true object
                const response = await Network.get(url);
                commit(`setWebsite`, response.data);
            } catch (e) {
                commit(`checkWebsiteError`, e);
            }
        },
        async refreshWebsite({state, commit}) {
            try {
                let url = `/api/public/website/${window.location.hostname}/${state.website.id}`;

                if (localStorage.getItem(`password`)) {
                    url += `?password=${localStorage.getItem(`password`)}`;
                }

                const response = await Network.get(url);
                commit(`setWebsite`, response.data);
            } catch (e) {
                commit(`checkWebsiteError`, e);
            }
        },
        // async reloadPages({state, commit, dispatch}, websiteId) {
        //     try {
        //         const resp = await Network.get(`/api/pages/website/${websiteId}`);
        //         commit(`setPages`, resp.data);
        //     } catch (e) {
        //         console.error(e);
        //         swal(`Network Error`, `Coulnd't load pages`, `error`);
        //     }
        //     state.applicationLoading = false;
        // },
        async loadPage({state, currentWebsite, commit, dispatch}, route) {
            state.applicationLoading = true;
            try {
                if (!state.applicationReady) {
                    await dispatch(`loadWebsite`, {domain: route?.query.domain});
                }
                let routeParams = CMS.parseRoute(route.fullPath);
                for (let redirect of state.website.routing?.redirects) {
                    if (!redirect.exactMatch && `/${routeParams.permalink}`.indexOf(redirect.from) === 0 || redirect.from === `/${routeParams.permalink}`) {
                        if (redirect.to.indexOf(`https`) === 0) {
                            return window.location.href = redirect.to;
                        } else {
                            return router.push(redirect.to);
                        }
                    }
                }
                if (state.pages[routeParams.permalink]) {
                    commit(`setPage`, {page: state.pages[routeParams.permalink], route});
                } else {
                    const response = await Network.get(`/api/public/pages/${state.website.dealerid}/permalink/${routeParams.permalink}`)
                    if (response.data) {
                        state.pages[routeParams.permalink] = response.data;
                    }
                    commit(`setPage`, {page: response.data, route});
                }
            } catch (e) {
                console.error(e);
                commit(`setPage`, null);
                swal(`Network Error`, `Couldn't load page`, `error`);
            }
            state.applicationLoading = false;
        },
        async getCurrentUser({commit, dispatch}) {
            commit(`startLogin`);
            let token = null;

            const currentURL = new URL(document.URL);
            const currentURLParams = new URLSearchParams(currentURL.search);

            if (currentURLParams.has(`token`)) {
                token = currentURLParams.get(`token`);
            }

            if (!token) {
                token = localStorage.getItem(`token`);
            }

            if (!token) {
                commit(`notConnected`);
                return false;
            }

            Network.setToken(token);
            NetworkV2.setToken(token);
            try {
                const response = await Network.get(`/users/current-user`, null, {system_api: true})
                commit(`authSuccess`, response.data);
            } catch (e) {
                console.error(e);
                dispatch(`logout`, {forceLogout: true});
            }
        },
        logout({commit}, payload = {}) {
            commit(`loggedOut`);
            localStorage.removeItem(`token`);
            Network.removeToken();
            NetworkV2.removeToken();

            const redirectURL = new URL(window.env.VUE_APP_URLS__LOGIN);
            redirectURL.searchParams.set(`redirect_url`, window.location.href);
            if (payload.forceLogout) {
                redirectURL.searchParams.set(`logout`, `1`);
            }

            window.location.href = redirectURL.toString();
        }
    },
    modules: {
        googleFonts: GoogleFontStore,
        mapEditor: MapStore,
        clipboard: ClipboardStore,
        assistant: AssistantStore,
        comparison: TextCompareStore
    }
});
